import { atom } from 'jotai';

/**
 * Atom to track if the scribe spotlight tooltip is currently open.
 * Used to manage the spotlight visibility state.
 */
export const scribeSpotlightOpenAtom = atom<boolean>(false);

/** True when a Work chat is bound, including on the landing URL. */
export const isAgentHomeProjectBoundAtom = atom(false);

// Promise-managed sidebar requests need an identity: two clicks can share a URL.
export const pendingNavRequestAtom = atom<symbol | null>(null);
const pendingNavHrefValueAtom = atom<string | null>(null);
export const pendingNavHrefAtom = atom(
  (get) => get(pendingNavHrefValueAtom),
  (get, set, next: string | null | ((previous: string | null) => string | null)) => {
    // Other navigation entry points still write only an href. Such a write
    // supersedes any promise-managed request, even for the same destination.
    set(pendingNavRequestAtom, null);
    set(
      pendingNavHrefValueAtom,
      typeof next === 'function' ? next(get(pendingNavHrefValueAtom)) : next
    );
  }
);

/**
 * Entry written by DiscoveryCallNavItem whenever its CTA is visible (sidebar IA).
 * Consumed by useCollapsedFooterDescriptors when the item collapses into the
 * primary nav More.
 */
export interface DiscoveryCallPrimaryEntry {
  href: string | undefined;
  label: string;
  onClick: () => void;
  testId: string;
}

/** True when Discovery Call has been collapsed into the primary nav More (sidebar IA). */
export const discoveryCallCollapsedIntoPrimaryAtom = atom<boolean>(false);

/** Resolved entry for the Discovery Call primary More row (sidebar IA). */
export const discoveryCallPrimaryEntryAtom = atom<DiscoveryCallPrimaryEntry | null>(null);

/**
 * True when Dictate has been collapsed into the primary nav More group
 * (sidebar IA: footer rail collapses → Dictate moves to primary More).
 */
export const dictateCollapsedIntoPrimaryAtom = atom<boolean>(false);

/** True when Help has been collapsed into the primary nav More (sidebar IA). */
export const helpCollapsedIntoPrimaryAtom = atom<boolean>(false);

/** True when Notifications has been collapsed into the primary nav More (sidebar IA). */
export const notificationsCollapsedIntoPrimaryAtom = atom<boolean>(false);

/** True when the credit usage slot has been collapsed into the primary nav More (sidebar IA). */
export const creditUsageCollapsedIntoPrimaryAtom = atom<boolean>(false);
/**
 * True when every primary nav fixed item (Home, Scribe, Library, …) has
 * collapsed into More (sidebar IA, very small sidebar). The footer rail only
 * starts collapsing its own items into the primary More after this is true.
 */
export const allPrimaryItemsCollapsedAtom = atom<boolean>(false);

/** Open signal for the notifications popover from the primary nav More row. */
export const notificationsPopoverOpenAtom = atom<boolean>(false);

/** Open signal for the Help dropdown from the primary nav More row. */
export const helpDropdownOpenAtom = atom<boolean>(false);
